import * as Sentry from '@sentry/solidstart';
import { useNavigate } from '@solidjs/router';
import { createContext, useContext, createSignal, Show, JSX, Component, onMount, onCleanup, ErrorBoundary, ComponentProps } from 'solid-js';
import {
  ACCOUNT_BLOCKED,
  AUTO_PAYMENT_ERRORS,
  CAN_NOT_FIND_THE_TRANSACTION_RECORD,
  CARD_LOST,
  CLOSE,
  COMPANY_NOT_ONBOARDED,
  COULD_NOT_FIND_PAYMENT_OPTIONS_FOR_THIS_LEASE,
  DUPLICATE_PAYMENT,
  EMAIL_IS_REQUIRED_FOR_MAGIC_LINK,
  EXPIRED_CARD,
  FAILED_TO_CONNECT,
  FAILED_TO_SUBSCRIBE_TO_CHAT_MESSAGES,
  FAILED_TO_UNSUBSCRIBE_FROM_CHAT_MESSAGES,
  INSUFFICIENT_FUNDS,
  INVALID_PAYMENT_AMOUNT,
  INVALID_PHONE_VERIFICATION_CODE_LENGTH,
  INVALID_USER_NAME_OR_PASSWORD,
  LEASE_RENEWAL_OFFER_NOT_FOUND,
  MAGIC_LINK_TOKEN_EXPIRED,
  MAXIMUM_AMOUNT_NUMBER,
  MESSAGE_DELIVERY_FAILED,
  NO_MAGIC_LINK_TOKEN,
  NO_MAINTENANCE_REQUEST_ID,
  NO_MAINTENANCE_REQUESTS,
  NO_PHONE_VERIFICATION_CODE,
  OVERPAYMENT_ERROR,
  PAYLOAD_TOO_LARGE,
  PAYMENT_FEATURE_IS_CURRENTLY_UNAVAILABLE,
  PHONE_NUMBER_IS_REQUIRED_FOR_PHONE_VERIFICATION,
  PLEASE_PROVIDE_A_DESCRIPTION_FOR_YOUR_REQUEST,
  PLEASE_UPLOAD_INSURANCE_POLICY_FILE,
  SELECT_A_PAYMENT_TYPE,
  SOMETHING_WENT_WRONG,
  STOLEN_CARD,
  THIS_LEASE_CURRENTLY_CAN_NOT_ACCEPTS_PAYMENTS,
  THIS_LEASE_CURRENTLY_CAN_NOT_PAY_WITH_CARDS,
  THIS_LEASE_CURRENTLY_CAN_NOT_PAY_WITH_ELECTRONIC_BANK_TRANSFER,
  TRANSFER_LIMIT_REACHED,
  VERIFICATION_CODE_EXPIRED,
  VERIFICATION_CODE_SENT_TOO_FREQUENTLY,
  YOU_CANNOT_PAY_LESS_THAN_THE_MINIMUM,
  YOU_DO_NOT_HAVE_ANY_CHATS,
  YOU_DO_NOT_HAVE_ANY_LEASES,
  YOU_DO_NOT_PROVIDE_A_TRANSACTION_ID,
  YOU_NEED_TO_CREATE_OR_SELECT_AN_AUTOPAYMENT,
  YOU_NEED_TO_PROVIDE_A_CHAT_ID,
  YOU_NEED_TO_PROVIDE_A_PAYMENT_ID,
  YOU_NEED_TO_RE_LINK_YOUR_PLAID_ACCOUNTS,
  YOU_NEED_TO_SELECT_A_LEASE,
  YOU_NEED_TO_SELECT_A_PAYMENT_AMOUNT,
  YOU_NEED_TO_SELECT_AN_ACCOUNT,
  YOU_NEED_TO_SPECIFY_AN_ACCOUNT_TO_MAKE_PAYMENTS_FROM,
} from '~/assets/strings';
import {
  AccountBlockedError,
  AccountNotSelectedError,
  AutopaymentFailedError,
  BankAccountNotLinkedError,
  CannotMakeAchPaymentError,
  CannotMakeCardPaymentError,
  CardLostError,
  DuplicationError,
  EmailIsRequiredForMagicLinkError,
  ExpiredCardError,
  FailedToConnectError,
  FailedToSubscribeToChatMessagesError,
  FailedToUnsubscribeFromChatMessagesError,
  HttpError,
  InsufficientFundsError,
  InsurancePolicyFileRequiredError,
  InvalidPaymentAmountError,
  InvalidPhoneVerificationCodeLengthError,
  InvalidUserNameAndPasswordError,
  LeaseCannotAcceptPaymentsError,
  MagicLinkTokenExpiredError,
  MessageDeliveryFailedError,
  NoAccountIdError,
  NoAutoPaymentIdError,
  NoAutopaymentSelectedError,
  NoChatSelectedError,
  NoChatsError,
  NoLeaseRenewalOfferError,
  NoLeaseSelectedError,
  NoLeasesError,
  NoMagicLinkTokenError,
  NoMaintenanceRequestDescriptionError,
  NoMaintenanceRequestError,
  NoMaintenanceRequestIdError,
  NoPaymentOptionsError,
  NoPhoneVerificationCodeError,
  NotFoundError,
  NotOnboardedError,
  NoTransactionIdError,
  NoTransactionRecordError,
  PayloadTooLargeError,
  PaymentAmountNotSelectedError,
  PaymentAmountOverRequestedError,
  PaymentAmountTooLowError,
  PaymentsDisabledError,
  PaymentTypeNotSelectedError,
  PhoneNumberIsRequiredForPhoneVerificationError,
  StolenCardError,
  TenantAccountNotMappedError,
  TransferLimitReachedError,
  UnauthorizedError,
  VerificationCodeExpiredError,
  VerificationCodeSentTooFrequentlyError,
} from '~/errors/errors';
import { eventEmitter } from '~/framework/eventEmitter';
import useUseCase from '~/framework/hooks/useUseCase';
import { currency } from '~/global-contexts/utils/number';
import { LogOutUseCase } from '~/use-cases/auth/logOutUseCase';
import { useLocalization } from '../../hooks/useLocalization';
interface ErrorContextType {
  error: () => Error | undefined;
}

const ErrorContext = createContext<ErrorContextType>();

interface ErrorHandlerProps {
  children: JSX.Element;
  fallback: ComponentProps<typeof ErrorBoundary>['fallback'];
}

export const ErrorHandler: Component<ErrorHandlerProps> = (props) => {
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [shouldDisplay, setShouldDisplay] = createSignal<boolean>(true);
  const navigate = useNavigate();
  const { t } = useLocalization();

  const { execute: signOut } = useUseCase(LogOutUseCase);

  const reportError = (error: Error, shouldDisplay?: boolean) => {
    sentryCaptureError(error);
    if (error instanceof UnauthorizedError) {
      signOut(location.pathname);
    } else if (error instanceof NotFoundError || error instanceof NotOnboardedError) {
      navigate('/tenants/404');
    } else {
      const display = shouldDisplay || shouldDisplay === undefined;
      setShouldDisplay(display);
      setError(error);
    }
  };

  const clearError = () => {
    setError(undefined);
  };

  onMount(() => {
    eventEmitter.registerForErrors(reportError);
  });

  onCleanup(() => {
    eventEmitter.unregisterFromErrors(reportError);
  });

  const getMessageForError = (error?: Error): string => {
    if (error instanceof HttpError && error.message) {
      return error.message;
    }
    if (error instanceof InvalidPaymentAmountError) {
      return t(INVALID_PAYMENT_AMOUNT, {
        minAmount: currency(error.getMinimumPaymentAmount(), { minimumFractionDigits: 2 }),
        maxAmount: MAXIMUM_AMOUNT_NUMBER,
      });
    }
    if (error instanceof FailedToConnectError) {
      return t(FAILED_TO_CONNECT);
    }
    if (error instanceof InvalidUserNameAndPasswordError) {
      return t(INVALID_USER_NAME_OR_PASSWORD);
    }
    if (error instanceof NotOnboardedError) {
      return t(COMPANY_NOT_ONBOARDED);
    }
    if (error instanceof AccountNotSelectedError) {
      return t(YOU_NEED_TO_SELECT_AN_ACCOUNT);
    }
    if (error instanceof PaymentTypeNotSelectedError) {
      return t(SELECT_A_PAYMENT_TYPE);
    }
    if (error instanceof PaymentAmountNotSelectedError) {
      return t(YOU_NEED_TO_SELECT_A_PAYMENT_AMOUNT);
    }
    if (error instanceof PaymentAmountTooLowError) {
      return t(YOU_CANNOT_PAY_LESS_THAN_THE_MINIMUM);
    }
    if (error instanceof NoLeaseSelectedError) {
      return t(YOU_NEED_TO_SELECT_A_LEASE);
    }
    if (error instanceof NoPaymentOptionsError) {
      return t(COULD_NOT_FIND_PAYMENT_OPTIONS_FOR_THIS_LEASE);
    }
    if (error instanceof PayloadTooLargeError) {
      return t(PAYLOAD_TOO_LARGE);
    }
    if (error instanceof InsurancePolicyFileRequiredError) {
      return t(PLEASE_UPLOAD_INSURANCE_POLICY_FILE);
    }
    if (error instanceof MessageDeliveryFailedError) {
      return t(MESSAGE_DELIVERY_FAILED);
    }
    if (error instanceof NoLeasesError) {
      return t(YOU_DO_NOT_HAVE_ANY_LEASES);
    }
    if (error instanceof LeaseCannotAcceptPaymentsError) {
      return t(THIS_LEASE_CURRENTLY_CAN_NOT_ACCEPTS_PAYMENTS);
    }
    if (error instanceof CannotMakeCardPaymentError) {
      return t(THIS_LEASE_CURRENTLY_CAN_NOT_PAY_WITH_CARDS);
    }
    if (error instanceof CannotMakeAchPaymentError) {
      return t(THIS_LEASE_CURRENTLY_CAN_NOT_PAY_WITH_ELECTRONIC_BANK_TRANSFER);
    }
    if (error instanceof NoAutopaymentSelectedError) {
      return t(YOU_NEED_TO_CREATE_OR_SELECT_AN_AUTOPAYMENT);
    }
    if (error instanceof AutopaymentFailedError) {
      return t(AUTO_PAYMENT_ERRORS);
    }
    if (error instanceof NoMaintenanceRequestIdError) {
      return t(NO_MAINTENANCE_REQUEST_ID);
    }
    if (error instanceof NoTransactionIdError) {
      return t(YOU_DO_NOT_PROVIDE_A_TRANSACTION_ID);
    }
    if (error instanceof NoTransactionRecordError) {
      return t(CAN_NOT_FIND_THE_TRANSACTION_RECORD);
    }
    if (error instanceof NoChatsError) {
      return t(YOU_DO_NOT_HAVE_ANY_CHATS);
    }
    if (error instanceof NoLeaseRenewalOfferError) {
      return t(LEASE_RENEWAL_OFFER_NOT_FOUND);
    }
    if (error instanceof NoChatSelectedError) {
      return t(YOU_NEED_TO_PROVIDE_A_CHAT_ID);
    }
    if (error instanceof FailedToSubscribeToChatMessagesError) {
      return t(FAILED_TO_SUBSCRIBE_TO_CHAT_MESSAGES);
    }
    if (error instanceof FailedToUnsubscribeFromChatMessagesError) {
      return t(FAILED_TO_UNSUBSCRIBE_FROM_CHAT_MESSAGES);
    }
    if (error instanceof NoMaintenanceRequestError) {
      return t(NO_MAINTENANCE_REQUESTS);
    }
    if (error instanceof NoMaintenanceRequestDescriptionError) {
      return t(PLEASE_PROVIDE_A_DESCRIPTION_FOR_YOUR_REQUEST);
    }
    if (error instanceof NoAutoPaymentIdError) {
      return t(YOU_NEED_TO_PROVIDE_A_PAYMENT_ID);
    }
    if (error instanceof NoAccountIdError) {
      return t(YOU_NEED_TO_SPECIFY_AN_ACCOUNT_TO_MAKE_PAYMENTS_FROM);
    }
    if (error instanceof InsufficientFundsError) {
      return t(INSUFFICIENT_FUNDS);
    }
    if (error instanceof BankAccountNotLinkedError) {
      return t(YOU_NEED_TO_RE_LINK_YOUR_PLAID_ACCOUNTS);
    }
    if (error instanceof CardLostError) {
      return t(CARD_LOST);
    }
    if (error instanceof TenantAccountNotMappedError) {
      return t(YOU_NEED_TO_RE_LINK_YOUR_PLAID_ACCOUNTS);
    }
    if (error instanceof TransferLimitReachedError) {
      return t(TRANSFER_LIMIT_REACHED);
    }
    if (error instanceof AccountBlockedError) {
      return t(ACCOUNT_BLOCKED);
    }
    if (error instanceof StolenCardError) {
      return t(STOLEN_CARD);
    }
    if (error instanceof ExpiredCardError) {
      return t(EXPIRED_CARD);
    }
    if (error instanceof DuplicationError) {
      return t(DUPLICATE_PAYMENT);
    }
    if (error instanceof PaymentAmountOverRequestedError) {
      return t(OVERPAYMENT_ERROR);
    }
    if (error instanceof PaymentsDisabledError) {
      return t(PAYMENT_FEATURE_IS_CURRENTLY_UNAVAILABLE);
    }
    if (error instanceof VerificationCodeSentTooFrequentlyError) {
      return t(VERIFICATION_CODE_SENT_TOO_FREQUENTLY);
    }
    if (error instanceof MagicLinkTokenExpiredError) {
      return t(MAGIC_LINK_TOKEN_EXPIRED);
    }
    if (error instanceof VerificationCodeExpiredError) {
      return t(VERIFICATION_CODE_EXPIRED);
    }
    if (error instanceof PhoneNumberIsRequiredForPhoneVerificationError) {
      return t(PHONE_NUMBER_IS_REQUIRED_FOR_PHONE_VERIFICATION);
    }
    if (error instanceof EmailIsRequiredForMagicLinkError) {
      return t(EMAIL_IS_REQUIRED_FOR_MAGIC_LINK);
    }
    if (error instanceof NoMagicLinkTokenError) {
      return t(NO_MAGIC_LINK_TOKEN);
    }
    if (error instanceof NoPhoneVerificationCodeError) {
      return t(NO_PHONE_VERIFICATION_CODE);
    }
    if (error instanceof InvalidPhoneVerificationCodeLengthError) {
      return t(INVALID_PHONE_VERIFICATION_CODE_LENGTH);
    }
    return t(SOMETHING_WENT_WRONG);
  };

  const sentryCaptureError = (error: Error) => {
    if (error instanceof UnauthorizedError) return;
    Sentry.captureException(error);
  };

  return (
    <ErrorBoundary
      fallback={(error, reset) => {
        sentryCaptureError(error);
        if (typeof props.fallback === 'function') {
          return props.fallback(error, reset);
        }
        return props.fallback;
      }}>
      <ErrorContext.Provider value={{ error }}>
        {props.children}
        <Show when={error() && shouldDisplay()}>
          <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/10 p-4">
            <div class="w-[500px] max-w-full overflow-hidden rounded-lg bg-white shadow-lg">
              <div class="border-b border-gray-200 px-6 py-4">
                <h2 class="text-center text-lg font-semibold text-primary-color">{'Oops!'}</h2>
              </div>
              <div class="p-6 py-4">
                <p class="text-center text-sm">{getMessageForError(error())}</p>
              </div>
              <div class="py-4 text-center">
                <button class="rounded bg-primary-color px-4 py-2 font-bold text-white hover:bg-hover-color" onClick={clearError}>
                  {t(CLOSE)}
                </button>
              </div>
            </div>
          </div>
        </Show>
      </ErrorContext.Provider>
    </ErrorBoundary>
  );
};

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorHandler');
  }
  return context;
}
